import { createColumnHelper } from "@tanstack/react-table";
import type { ColumnDef } from "../table";
import { displayCurrencyAmount } from "../../utils/display";
import { ReceivablesNotesCell } from "./ReceivablesNotesCell";
import {
  ReceivablesCustomerContactHint,
  ReceivablesEmailDisabledIcon,
  ReceivablesUnreadDot,
} from "./ReceivablesRowIndicators";
import type { CustomerReceivablesRow } from "./types";

const columnHelper = createColumnHelper<CustomerReceivablesRow>();

const amountColumn = (
  id: keyof CustomerReceivablesRow,
  header: string,
  size = 140,
) =>
  columnHelper.accessor(id, {
    id,
    header,
    enableSorting: true,
    size,
    meta: {
      align: "end",
      cellClassName: "whitespace-nowrap tabular-nums",
    },
    cell: ({ row }) =>
      displayCurrencyAmount(row.getValue(id) as number, row.original.currency),
  });

export const receivablesCustomerSelectionColumn: ColumnDef<
  CustomerReceivablesRow,
  unknown
> = {
  id: "select",
  header: ({ table }) => (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-slate-300"
      checked={table.getIsAllPageRowsSelected()}
      ref={(el) => {
        if (!el) return;
        el.indeterminate =
          table.getIsSomePageRowsSelected() &&
          !table.getIsAllPageRowsSelected();
      }}
      onChange={table.getToggleAllPageRowsSelectedHandler()}
      onClick={(event) => event.stopPropagation()}
      aria-label="Select all customers on this page"
    />
  ),
  cell: ({ row }) => (
    <input
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-slate-300"
      checked={row.getIsSelected()}
      onChange={row.getToggleSelectedHandler()}
      onClick={(event) => event.stopPropagation()}
      aria-label="Select customer"
    />
  ),
  enableSorting: false,
  enableResizing: false,
  size: 40,
  meta: {
    stopRowClick: true,
  },
};

export const receivablesCustomerEmailStatusColumn: ColumnDef<
  CustomerReceivablesRow,
  unknown
> = columnHelper.display({
  id: "emailStatus",
  header: "",
  enableSorting: false,
  enableResizing: false,
  size: 36,
  meta: {
    align: "center",
  },
  cell: ({ row }) => (
    <ReceivablesEmailDisabledIcon
      disabled={!row.original.isTemplateSending}
      title="Template sending disabled"
    />
  ),
});

export const receivablesCustomersColumns: ColumnDef<
  CustomerReceivablesRow,
  unknown
>[] = [
  columnHelper.accessor("customerName", {
    header: "Customer Name",
    enableSorting: true,
    sortingFn: "alphanumeric",
    size: 200,
    meta: {
      align: "start",
      cellClassName: "overflow-hidden font-semibold text-blue-700",
    },
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-2">
        <ReceivablesUnreadDot show={row.original.hasUnreadCorrespondence} />
        <ReceivablesCustomerContactHint
          name={row.original.customerName}
          email={row.original.primaryEmail}
          phone={row.original.primaryPhone}
        >
          <span className="block truncate">{row.original.customerName}</span>
        </ReceivablesCustomerContactHint>
      </span>
    ),
  }),
  columnHelper.accessor("accountingSoftwareId", {
    header: "Client ID",
    enableSorting: true,
    sortingFn: "alphanumeric",
    size: 220,
    minSize: 140,
    meta: {
      align: "start",
      cellClassName: "overflow-hidden text-slate-600 tabular-nums",
    },
    cell: ({ getValue }) => {
      const value = String(getValue() ?? "");
      return (
        <span className="block truncate" title={value}>
          {value}
        </span>
      );
    },
  }),
  amountColumn("currentAmount", "Current"),
  amountColumn("oneToThirtyDaysAmount", "1 - 30"),
  amountColumn("thirtyOneToSixtyDaysAmount", "31 - 60"),
  amountColumn("sixtyOneToNinetyDaysAmount", "61 - 90"),
  amountColumn("ninetyOneOrMoreDaysAmount", "91 and Over"),
  columnHelper.accessor("totalOpenBalance", {
    id: "totalOpenBalance",
    header: "Total",
    enableSorting: true,
    size: 140,
    meta: {
      align: "end",
      cellClassName: "whitespace-nowrap tabular-nums font-medium",
    },
    cell: ({ row }) =>
      displayCurrencyAmount(
        row.original.totalOpenBalance,
        row.original.currency,
      ),
  }),
  columnHelper.accessor("notes", {
    header: "Notes",
    enableSorting: true,
    sortingFn: "alphanumeric",
    size: 180,
    meta: {
      align: "start",
      cellClassName: "truncate",
    },
    cell: ({ row }) => <ReceivablesNotesCell notes={row.original.notes} />,
  }),
];
